import React, { useRef, useState } from "react"

import { useEmptyImage } from "shared/lib/useEmptyImage"
import { useEvent } from "shared/lib/useEvent"

import { SwipeToDeleteProps } from "../SwipeToDelete"

export function useSwipeToDelete({
  contentRef,
  dragThreshold = 10,
  deleteThreshold = 50,
  onDelete,
  onDragEnd,
  onDragStart
}: SwipeToDeleteProps & {
  contentRef: React.RefObject<HTMLDivElement | null>
}) {
  const [xOffset, setXOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const dragStartCoordinates = useRef({ x: 0, y: 0 })
  const dragStartDimensions = useRef({ width: 0, height: 0 })

  const img = useEmptyImage()

  const handleSwipeStart = useEvent((x: number, y: number) => {
    dragStartCoordinates.current = { x, y }
    const contentRect = contentRef.current?.getBoundingClientRect()
    dragStartDimensions.current = {
      height: contentRect?.height || 0,
      width: contentRect?.width || 0
    }
  })

  const handleSwipe = useEvent((x: number, y: number) => {
    const movementX = dragStartCoordinates.current.x - x
    const movementY = Math.abs(dragStartCoordinates.current.y - y)

    if (!isDragging && !isScrolling && movementY >= dragThreshold) {
      setIsScrolling(true)
    } else if (!isScrolling && !isDragging && movementX >= dragThreshold) {
      setIsDragging(true)
      onDragStart?.()
    } else if (isDragging && !isScrolling && movementX !== xOffset) {
      setXOffset(Math.max(movementX, 0))
    }
  })

  const handleSwipeEnd = useEvent(() => {
    setIsDragging(false)
    setIsScrolling(false)
    if (xOffset >= deleteThreshold) {
      onDelete?.()
    }
    setXOffset(0)
    dragStartCoordinates.current = { x: 0, y: 0 }
    onDragEnd?.()
  })

  const dragEventListener = useEvent((ev: DragEvent) => {
    if (isDragging) {
      ev.preventDefault()
    }

    handleSwipe(ev.pageX, ev.pageY)
  })

  const touchEventListener = useEvent((ev: TouchEvent) => {
    if (isDragging) {
      ev.preventDefault()
    }

    handleSwipe(ev.touches[0].clientX, ev.touches[0].clientY)
  })

  const handleDragStart: React.DragEventHandler<HTMLDivElement> = useEvent(
    ev => {
      if (ev?.dataTransfer?.setDragImage) {
        ev.dataTransfer.setDragImage(img, 0, 0)
      }
      handleSwipeStart(ev.pageX, ev.pageY)
      window.addEventListener("dragover", dragEventListener)
    }
  )

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = useEvent(
    ev => {
      ev.preventDefault()
      handleSwipeStart(ev.touches[0].clientX, ev.touches[0].clientY)
      window.addEventListener("touchmove", touchEventListener, {
        passive: false
      })
    }
  )

  const handleDragEnd = useEvent(() => {
    handleSwipeEnd()
    window.removeEventListener("dragover", dragEventListener)
  })

  const handleTouchEnd = useEvent(() => {
    handleSwipeEnd()
    window.removeEventListener("touchmove", touchEventListener)
  })

  return {
    onDragStart: handleDragStart,
    onTouchStart: handleTouchStart,
    onDragEnd: handleDragEnd,
    onTouchEnd: handleTouchEnd,
    willDelete: xOffset >= deleteThreshold,
    xOffset: Math.min(xOffset, dragStartDimensions.current.width),
    isDragging,
    isScrolling
  }
}
